import React from 'react';
import { PrismicRichText } from '@prismicio/react';
import styled from 'styled-components';
import { BREAKPOINT } from '@constants';
import { SliceBannerGridItem } from '@contracts';
import Colors from '../Colors';
import LazyLoadImage from '../LazyLoadImage';
import LinkButton from '../LinkButton';
import useDesktop from '../useDesktop';
import { TEXT_STYLES, TextVariant, Typography } from './Typography';

interface BannerGridItemProps {
  item: SliceBannerGridItem;
}

const BannerGridItem: React.FC<BannerGridItemProps> = ({ item }) => {
  const desktop = useDesktop(768);

  return (
    <GridItem $background={item.background_text ?? Colors.sage50} $image={item.background_image?.url ?? ''}>
      {item?.image?.url ? (
        <ImageWrapper>
          <LazyLoadImageStyled src={item?.image?.url ?? ''} alt={item?.heading?.text ?? ''} />
        </ImageWrapper>
      ) : null}
      <GridItemBody>
        <TextWrapper>
          {item?.overline && (
            <Typography $variant={TextVariant.UpperText4} $color={item.textcolor}>
              {item.overline}
            </Typography>
          )}
          {item.heading.richText?.length > 0 && (
            <Heading>
              <PrismicRichText field={item.heading.richText} />
            </Heading>
          )}
        </TextWrapper>
        {item?.buttontext && (
          <LinkButton size={desktop ? 'normal' : 'small'} backgroundColor={Colors.lilac} textColor={Colors.cream} to={item.button_url}>
            {item.buttontext}
          </LinkButton>
        )}
      </GridItemBody>
    </GridItem>
  );
};

export default BannerGridItem;

const DESKTOP_HEIGHT = 608;
const MOBILE_HEIGHT = 486;

const GridItem = styled.div<{ $background?: string; $image?: string }>`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  border-radius: 12px;
  overflow: hidden;
  height: ${DESKTOP_HEIGHT}px;
  position: relative;
  background-color: ${(props) => props.$background || Colors.sage50};
  background-image: ${(props) => (props.$image ? `url(${props.$image})` : 'none')};
  background-repeat: no-repeat;
  background-position: center bottom;
  background-size: cover;
  padding: 40px 24px;

  @media (max-width: ${BREAKPOINT.mobileL}px) {
    padding: 32px 24px;
    height: ${MOBILE_HEIGHT}px;
  }
`;

const ImageWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  flex-direction: column;
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
`;

const LazyLoadImageStyled = styled(LazyLoadImage)`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: bottom;
  z-index: 1;
`;

const GridItemBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  row-gap: 24px;
  flex: 1;
  position: relative;
  z-index: 3;
  max-width: 400px;

  @media (max-width: 450px) {
    row-gap: 16px;
  }

  & > a:hover {
    background: ${Colors.navyBlue};
    color: ${Colors.cream};
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Heading = styled.div`
  > * {
    color: ${Colors.navyBlue};
    ${TEXT_STYLES[TextVariant.Heading5]};

    @media (max-width: ${BREAKPOINT.mobileL}px) {
      ${TEXT_STYLES[TextVariant.Heading6]};
    }
  }
`;
